import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function AboutPage() {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const advantages = [
    {
      icon: 'ri-movie-2-line',
      title: '海量资源',
      desc: '超过10万部高清影视资源，涵盖电影、电视剧、综艺、动漫等多种类型，满足不同用户的观影需求。',
    },
    {
      icon: 'ri-4k-line',
      title: '超清画质',
      desc: '支持1080P至4K超高清画质播放，搭配智能码率自适应技术，根据网络状况自动优化观影体验。',
    },
    {
      icon: 'ri-device-line',
      title: '多端同步',
      desc: '支持PC、手机、平板、智能电视等多终端无缝切换，观影记录云端同步，随时随地继续观看。',
    },
    {
      icon: 'ri-shield-check-line',
      title: '安全可靠',
      desc: '采用先进的加密技术和内容分发网络，确保观影过程安全流畅，保护用户隐私和数据安全。',
    },
    {
      icon: 'ri-customer-service-2-line',
      title: '贴心服务',
      desc: '7×24小时客服支持，快速响应用户反馈，持续优化产品体验，让每一次观影都成为享受。',
    },
    {
      icon: 'ri-rocket-line',
      title: '极速更新',
      desc: '专业内容团队每日更新最新影视资源，热门大片和剧集第一时间上线，确保用户不错过任何精彩。',
    },
  ];

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    const honeypotField = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9u46bv8l97n3eas3gvg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg =
          parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (typeof serverMsg === 'string' && serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
          setFormStatus('error');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />
      <main>
        <div className="pt-20 pb-6 bg-background-100">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: t('nav.about') }]} />
          </div>
        </div>

        <section className="max-w-[1200px] mx-auto px-4 md:px-6 py-16">
          <div className="text-center mb-16">
            <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
              {t('about.title')}
            </h1>
            <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
              蜜桃影视成立于2020年，是国内领先的在线影视内容聚合平台。我们致力于通过技术创新和优质内容，为数千万用户提供极致的在线观影体验，让高品质的影视娱乐触手可及。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 mb-20">
            {advantages.map((item, index) => (
              <div
                key={index}
                className="bg-background-50 border border-background-200 rounded-xl p-6 hover:border-primary-200 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                  <i className={`${item.icon} text-primary-600 text-xl`}></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-background-100 py-16 md:py-20">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                  {t('about.contact_us')}
                </h2>
                <div className="space-y-4 text-sm text-foreground-600">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900">电子邮箱</p>
                      <p>contact@mitaomovies.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900">客服电话</p>
                      <p>400-888-9999</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900">公司地址</p>
                      <p>北京市朝阳区光华路5号世纪财富中心18层</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 bg-accent-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className="ri-time-line text-accent-600"></i>
                    </div>
                    <div>
                      <p className="font-medium text-foreground-900">工作时间</p>
                      <p>周一至周日 9:00 - 21:00</p>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <div className="bg-background-50 border border-background-200 rounded-xl p-6 md:p-8">
                  <h3 className="text-lg font-heading font-semibold text-foreground-950 mb-6">
                    发送消息
                  </h3>
                  <form
                    id="about-contact-form"
                    data-readdy-form
                    onSubmit={handleSubmit}
                    className="space-y-4"
                  >
                    <input
                      type="text"
                      name="phone_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                      className="form-honeypot"
                    />
                    <div>
                      <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        id="contact-name"
                        name="name"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        id="contact-email"
                        name="email"
                        type="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        主题
                      </label>
                      <input
                        id="contact-subject"
                        name="subject"
                        type="text"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors"
                        placeholder="请输入主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                        留言
                      </label>
                      <textarea
                        id="contact-message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 transition-colors resize-none"
                        placeholder="请输入您的留言（最多500字）"
                      ></textarea>
                      <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                    </div>
                    <button
                      type="submit"
                      disabled={formStatus === 'loading'}
                      className="w-full bg-primary-500 hover:bg-primary-600 disabled:bg-primary-300 text-white py-2.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
                    >
                      {formStatus === 'loading' ? '发送中...' : '发送消息'}
                    </button>
                    {formStatus === 'success' && (
                      <p className="text-sm text-green-600 text-center">消息已发送成功！我们会尽快回复您。</p>
                    )}
                    {formStatus === 'error' && formError && (
                      <p className="text-sm text-red-500 text-center">{formError}</p>
                    )}
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'AboutPage',
              name: '关于蜜桃影视',
              description: '蜜桃影视是国内领先的在线影视内容聚合平台，致力于通过技术创新和优质内容，为数千万用户提供极致的在线观影体验。',
              url: 'https://entervue.github.io/about',
              isPartOf: {
                '@type': 'WebSite',
                name: '蜜桃影视',
                url: 'https://entervue.github.io',
              },
              breadcrumb: {
                '@type': 'BreadcrumbList',
                itemListElement: [
                  { '@type': 'ListItem', position: 1, name: '首页', item: 'https://entervue.github.io/' },
                  { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://entervue.github.io/about' },
                ],
              },
              about: {
                '@type': 'Organization',
                name: '蜜桃影视',
                url: 'https://entervue.github.io',
                contactPoint: {
                  '@type': 'ContactPoint',
                  telephone: '+86-400-888-9999',
                  contactType: 'customer service',
                  availableLanguage: 'Chinese',
                },
              },
            }),
          }}
        />
      </main>
      <Footer />
    </>
  );
}